import { NavLink, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "@nbs/ui";
import { BrandMark } from "@/components/BrandMark";
import { defaultPathForLevel } from "@/features/access/access-level";
import { useAccessLevel } from "@/features/access/AccessLevelContext";
import { useOnboarding } from "@/features/onboarding/OnboardingProvider";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useTheme } from "@/app/providers/ThemeProvider";
import { MainNav } from "./MainNav";

const SUPPORT_EMAIL = "azizmashkour@gmail.com";
/** Support ticket reference — must be included in contact emails. */
const LICENSE_REFERENCE_CODE = "NBS-HOST-4Y-92K7";

function LicenseBannerIcon() {
  return (
    <svg
      className="nbs-header__license-ico"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3 4.5 6.5v5.2c0 4.6 3.1 7.9 7.5 9.3 4.4-1.4 7.5-4.7 7.5-9.3V6.5L12 3Z" />
      <path d="M9.2 12.1 11 13.9l3.8-3.8" />
    </svg>
  );
}

export function Header({ onToggleNav, navOpen }: { onToggleNav: () => void; navOpen: boolean }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const permissions = usePermissions();
  const theme = useTheme();
  const onboarding = useOnboarding();
  const navigate = useNavigate();
  const { level, openPrompt } = useAccessLevel();
  const homePath = level ? defaultPathForLevel(level) : "/";
  const showLicenseBanner = permissions.roles.includes("ADMIN");
  const supportMailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    `Support Écosystèmes Guinée — ${LICENSE_REFERENCE_CODE}`,
  )}&body=${encodeURIComponent(`Code de référence : ${LICENSE_REFERENCE_CODE}\n\n`)}`;

  function handleLogout() {
    auth.logout();
    void navigate(homePath, { replace: true });
  }

  return (
    <header className={`nbs-header${showLicenseBanner ? " nbs-header--with-license" : ""}`}>
      {showLicenseBanner ? (
        <div className="nbs-header__license" role="status">
          <LicenseBannerIcon />
          <p className="nbs-header__license-text">
            <Trans
              i18nKey="shell.licenseBanner"
              values={{ email: SUPPORT_EMAIL, code: LICENSE_REFERENCE_CODE }}
              components={{
                years: <strong className="nbs-header__license-years" />,
                ref: <strong className="nbs-header__license-code" />,
                contact: <a className="nbs-header__license-link" href={supportMailto} />,
              }}
            />
          </p>
        </div>
      ) : null}
      <div className="nbs-header__bar">
        <div className="nbs-header__brand">
          <Button
            variant="ghost"
            size="sm"
            className="nbs-header__menu"
            aria-expanded={navOpen}
            aria-controls="nbs-sidebar"
            onClick={onToggleNav}
          >
            {navOpen ? t("shell.closeMenu") : t("shell.openMenu")}
          </Button>
          <NavLink to={homePath} className="nbs-header__logo">
            <BrandMark size={18} className="nbs-header__mark" />
            {t("app.name")}
          </NavLink>
        </div>
        <MainNav className="nbs-header__nav nbs-mainnav" dataTestId="app-mainnav" />
        <div className="nbs-header__actions">
          <Button variant="ghost" size="sm" data-testid="header-change-access" onClick={openPrompt}>
            {t("nav.changeAccess")}
          </Button>
          {auth.isAuthenticated ? (
            <Button
              variant="ghost"
              size="sm"
              className="nbs-header__tour"
              data-tour="tour-launch"
              aria-label={t("onboarding.launchAria")}
              title={t("onboarding.launch")}
              disabled={onboarding.active}
              onClick={onboarding.start}
            >
              <span aria-hidden="true" className="nbs-header__tour-ico">
                ?
              </span>
              <span className="nbs-header__tour-label">{t("onboarding.launch")}</span>
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            data-tour="theme-toggle"
            onClick={theme.toggle}
            aria-label={theme.resolved === "dark" ? t("shell.themeLight") : t("shell.themeDark")}
          >
            {theme.resolved === "dark" ? "☀" : "☾"}
          </Button>
          {auth.isAuthenticated ? (
            <>
              <span className="nbs-header__user nbs-text nbs-text--sm">
                {auth.user?.displayName}
              </span>
              <Button variant="secondary" size="sm" onClick={handleLogout}>
                {t("nav.logout")}
              </Button>
            </>
          ) : (
            <NavLink to="/login" data-testid="header-login-link">
              <Button variant="secondary" size="sm">
                {t("nav.login")}
              </Button>
            </NavLink>
          )}
        </div>
      </div>
    </header>
  );
}
